/**
 * Where to land after signing in.
 *
 * A signed-out person opening a deep link (the « Répondre » button of a
 * walk-in email → `/arrivee/<id>`) is bounced to `/sign-in?redirect=<path>`
 * by the dashboard layout, and sign-in sends them back there instead of `/`.
 *
 * The value arrives in the URL, so it is untrusted: only a same-origin path
 * is honoured. `//host` and `/\host` are protocol-relative to a browser, so
 * both are refused along with anything absolute. `/` and `/sign-in` itself
 * return undefined — the default landing, and never a loop.
 */
export function safeRedirectPath(raw: unknown): string | undefined {
  if (typeof raw !== "string") return undefined;
  if (!raw.startsWith("/")) return undefined;
  if (raw.startsWith("//") || raw.startsWith("/\\")) return undefined;
  if (raw === "/" || raw === "/sign-in" || raw.startsWith("/sign-in?")) {
    return undefined;
  }
  if (raw.startsWith("/sign-in/")) return undefined;
  // Control characters and backslashes anywhere: a URL parser strips tab and
  // newline and reads `\` as `/`, so « /\t/evil.com » becomes « //evil.com ».
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\\]/.test(raw)) return undefined;
  // Belt and braces: whatever the browser makes of it must stay same-origin.
  try {
    if (new URL(raw, "http://x").origin !== "http://x") return undefined;
  } catch {
    return undefined;
  }
  return raw;
}
